import { useEffect, useRef } from 'react';
import { touchActivity, checkSessionExpiry } from '../utils/sessionTimeout';

const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'pointerdown'];
const ACTIVITY_THROTTLE_MS = 30 * 1000; // avoid hammering localStorage on every interaction
const CHECK_INTERVAL_MS = 30 * 1000; // catches an idle-but-open tab; a bare setTimeout
// wouldn't survive the tab being backgrounded/closed, so we re-derive expiry from
// stored timestamps instead of trusting a single long-lived timer.

/**
 * App-wide idle + absolute session timeout tracker. Mount once (e.g. in App()),
 * gated on whether a user is currently logged in.
 *
 * Deliberately does not listen for mousemove — too noisy for a 30s-throttled
 * write, clicks/keys/scroll/navigation are enough signal for genuine activity.
 */
export default function useSessionTimeout(isLoggedIn, onExpire) {
  const lastTouchRef = useRef(0);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    const runCheck = () => {
      const reason = checkSessionExpiry();
      if (reason) {
        onExpireRef.current(reason);
      }
    };

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastTouchRef.current < ACTIVITY_THROTTLE_MS) return;
      lastTouchRef.current = now;
      touchActivity();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        runCheck();
      }
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));
    window.addEventListener('focus', runCheck);
    document.addEventListener('visibilitychange', handleVisibility);

    const intervalId = setInterval(runCheck, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
      window.removeEventListener('focus', runCheck);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(intervalId);
    };
  }, [isLoggedIn]);
}
